import React, { useState } from 'react';
import './ButtonWidgetStyle.css';
import SliderSection from './SliderSection';
import FontSelector from './FontSelector';

// 🎨 BUTTON ANIMATIONS - Gentle Animations wie im README
const buttonAnimations = [
    { id: 'none', name: 'None', css: '' },
    { id: 'gentle-pulse', name: 'Gentle Pulse', css: 'gentle-pulse 2s infinite' },
    { id: 'gentle-sway', name: 'Gentle Sway', css: 'gentle-sway 3s infinite' },
    { id: 'subtle-float', name: 'Subtle Float', css: 'subtle-float 4s infinite' },
    { id: 'gentle-breathe', name: 'Gentle Breathe', css: 'gentle-breathe 3s infinite' },
    { id: 'gentle-wobble', name: 'Gentle Wobble', css: 'gentle-wobble 2s infinite' }
];

function ButtonWidgetStyle({ 
    editData, 
    handleInputChange,
    buttonConfig = {
        textField: 'buttonText',
        linkField: 'buttonLink'
    }
}) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [activeDevice, setActiveDevice] = useState('desktop'); // 'desktop' or 'mobile'

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    const handleDeviceSwitch = (device) => {
        setActiveDevice(device);
    };

    // Helper function to get device-specific field name
    const getDeviceField = (baseField) => {
        if (activeDevice === 'mobile') {
            return `${baseField}Mobile`;
        }
        return baseField;
    };

    // Helper function to get current value for device
    const getCurrentValue = (baseField, defaultValue) => {
        const deviceField = getDeviceField(baseField);
        const deviceValue = editData[deviceField];
        const baseValue = editData[baseField];
        
        // ✅ FIREBASE-SAFE: Ensure we never return undefined
        if (deviceValue !== undefined && deviceValue !== null && deviceValue !== '') {
            return deviceValue;
        }
        if (baseValue !== undefined && baseValue !== null && baseValue !== '') {
            return baseValue;
        }
        
        // ✅ ERWEITERTE FALLBACK-LOGIK für verschiedene Field-Types
        if (typeof defaultValue === 'number') {
            return defaultValue;
        }
        if (typeof defaultValue === 'string') {
            return defaultValue;
        }
        
        // ✅ FIELD-SPEZIFISCHE DEFAULTS
        switch (baseField) {
            case 'buttonColor': return '#5C80BC';
            case 'buttonTextColor': return '#ffffff';
            case 'buttonTextSize': return 16;
            case 'buttonTextWeight': return '600';
            case 'buttonWidth': return 80; // Prozent
            case 'buttonWidthDesktop': return 200; // Pixel
            case 'buttonHeight': return 50;
            case 'buttonBorderRadius': return 8;
            case 'buttonBorderWidth': return 2;
            case 'buttonBorderColor': return '#5C80BC';
            case 'buttonAnimation': return 'none';
            case 'buttonText': return 'Button Text';
            case 'buttonLink': return '';
            // ✅ GRADIENT SYSTEM DEFAULTS
            case 'buttonGradientColor1': return '#667eea';
            case 'buttonGradientColor2': return '#764ba2';
            // ✅ SHADOW SYSTEM DEFAULTS
            case 'buttonShadowIntensity': return 15;
            case 'buttonShadowDistance': return 8;
            case 'buttonShadowOpacity': return 25;
            case 'buttonShadowColor': return '#000000';
            // ✅ ANIMATION SYSTEM DEFAULTS
            case 'buttonAnimationType': return 'gentle-pulse';
            case 'buttonAnimationSpeed': return 2000;
            default: return defaultValue || 0;
        }
    };

    // Helper function to handle device-specific changes
    const handleDeviceChange = (fieldName, value) => {
        // ✅ FIREBASE-SAFE: Ensure we never send undefined values
        const safeValue = value !== undefined && value !== null ? value : 0;
        
        // ✅ NEUE LOGIK: Für buttonWidthDesktop direkt verwenden, sonst Device-Suffix
        let targetFieldName;
        if (fieldName === 'buttonWidthDesktop' || fieldName === 'buttonWidthMobile' || fieldName === 'buttonMaxWidthMobile') {
            targetFieldName = fieldName; // Direkte Verwendung für neue Properties
        } else {
            targetFieldName = getDeviceField(fieldName); // Alte Logik für andere Properties
        }
        
        const fakeEvent = {
            target: {
                name: targetFieldName,
                value: safeValue,
                type: 'range'
            }
        };
        handleInputChange(fakeEvent);
    };

    // Helper function to convert color to hex
    const colorToHex = (color) => {
        if (!color) return '#000000';
        if (color.startsWith('#')) return color.toUpperCase();
        // Convert rgb/rgba to hex if needed
        return color;
    };

    // Helper function to handle hex input changes
    const handleHexChange = (fieldName, hexValue) => {
        let cleanHex = hexValue.replace('#', '').toUpperCase();
        if (cleanHex.length === 6 && /^[0-9A-F]{6}$/i.test(cleanHex)) {
            const fakeEvent = {
                target: {
                    name: fieldName,
                    value: `#${cleanHex}`,
                    type: 'color'
                }
            };
            handleInputChange(fakeEvent);
        }
    };

    return (
        <div className="button-widget-style-container">
            {/* Sleek Header mit modernem Toggle */}
            <div 
                className={`button-style-header ${isExpanded ? 'expanded' : ''}`}
                onClick={toggleExpanded}
            >
                <div className="button-style-header-content">
                    <div className="button-style-icon">
                        <i className="fa fa-square"></i>
                    </div>
                    <div className="button-style-title">Button Style</div>
                </div>
                <div className={`button-style-toggle-arrow ${isExpanded ? 'rotated' : ''}`}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path 
                            d="M4 6L8 10L12 6" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
                <div className="button-style-content-expanded">
                    
                    {/* 🎨 COLORS SECTION mit runden Color-Pickern + Hex-Inputs */}
                    <div className="button-colors-section">
                        <div className="colors-header">
                            <i className="fa fa-palette"></i>
                            <span>Colors</span>
                        </div>
                        
                        <div className="colors-grid">
                            <div className="color-container">
                                <label className="color-label">Background</label>
                                <div className="color-picker-wrapper">
                                    <input
                                        type="color"
                                        name="buttonColor"
                                        value={getCurrentValue('buttonColor', '#5C80BC')}
                                        onChange={handleInputChange}
                                        className="hidden-color-input"
                                        id="buttonColorPicker"
                                    />
                                    <label htmlFor="buttonColorPicker" className="color-display-circle" 
                                           style={{ backgroundColor: getCurrentValue('buttonColor', '#5C80BC') }}>
                                    </label>
                                </div>
                                <input
                                    type="text"
                                    className="hex-input"
                                    value={colorToHex(getCurrentValue('buttonColor', '#5C80BC'))}
                                    onChange={(e) => handleHexChange('buttonColor', e.target.value)}
                                    placeholder="#5C80BC"
                                />
                            </div>
                            
                            <div className="color-container">
                                <label className="color-label">Text</label>
                                <div className="color-picker-wrapper">
                                    <input
                                        type="color"
                                        name="buttonTextColor"
                                        value={getCurrentValue('buttonTextColor', '#ffffff')}
                                        onChange={handleInputChange}
                                        className="hidden-color-input"
                                        id="buttonTextColorPicker"
                                    />
                                    <label htmlFor="buttonTextColorPicker" className="color-display-circle" 
                                           style={{ backgroundColor: getCurrentValue('buttonTextColor', '#ffffff') }}>
                                    </label>
                                </div>
                                <input
                                    type="text"
                                    className="hex-input"
                                    value={colorToHex(getCurrentValue('buttonTextColor', '#ffffff'))}
                                    onChange={(e) => handleHexChange('buttonTextColor', e.target.value)}
                                    placeholder="#FFFFFF"
                                />
                            </div>
                        </div>
                    </div>

                    {/* 🌈 GRADIENT SECTION */}
                    <div className="gradient-section">
                        <div className="gradient-toggle-row">
                            <label className="gradient-toggle-label">Enable Gradient</label>
                            <div className="gradient-toggle-switch-container">
                                <input
                                    type="checkbox"
                                    name="buttonGradientEnabled"
                                    checked={editData.buttonGradientEnabled || false}
                                    onChange={handleInputChange}
                                    className="gradient-toggle-input"
                                    id="buttonGradientEnabledToggle"
                                />
                                <label htmlFor="buttonGradientEnabledToggle" className="gradient-toggle-switch">
                                    <span className="gradient-toggle-slider"></span>
                                </label>
                            </div>
                        </div>

                        {/* Gradient Colors (conditional) */}
                        {editData.buttonGradientEnabled && (
                            <>
                                <div className="gradient-colors-grid">
                                    <div className="color-container">
                                        <label className="color-label">Color 1</label>
                                        <div className="color-picker-wrapper">
                                            <input
                                                type="color"
                                                name="buttonGradientColor1"
                                                value={getCurrentValue('buttonGradientColor1', '#667eea')}
                                                onChange={handleInputChange}
                                                className="hidden-color-input"
                                                id="buttonGradientColor1Picker"
                                            />
                                            <label htmlFor="buttonGradientColor1Picker" className="color-display-circle" 
                                                   style={{ backgroundColor: getCurrentValue('buttonGradientColor1', '#667eea') }}>
                                            </label>
                                        </div>
                                        <input
                                            type="text"
                                            className="hex-input"
                                            value={colorToHex(getCurrentValue('buttonGradientColor1', '#667eea'))}
                                            onChange={(e) => handleHexChange('buttonGradientColor1', e.target.value)}
                                            placeholder="#667EEA"
                                        />
                                    </div>
                                    
                                    <div className="color-container">
                                        <label className="color-label">Color 2</label>
                                        <div className="color-picker-wrapper">
                                            <input
                                                type="color"
                                                name="buttonGradientColor2"
                                                value={getCurrentValue('buttonGradientColor2', '#764ba2')}
                                                onChange={handleInputChange}
                                                className="hidden-color-input"
                                                id="buttonGradientColor2Picker"
                                            />
                                            <label htmlFor="buttonGradientColor2Picker" className="color-display-circle" 
                                                   style={{ backgroundColor: getCurrentValue('buttonGradientColor2', '#764ba2') }}>
                                            </label>
                                        </div>
                                        <input
                                            type="text"
                                            className="hex-input"
                                            value={colorToHex(getCurrentValue('buttonGradientColor2', '#764ba2'))}
                                            onChange={(e) => handleHexChange('buttonGradientColor2', e.target.value)}
                                            placeholder="#764BA2"
                                        />
                                    </div>
                                </div>

                                {/* Gradient Animation Toggle */}
                                <div className="gradient-animation-row">
                                    <label className="gradient-animation-label">Gradient Animation</label>
                                    <div className="gradient-toggle-switch-container">
                                        <input
                                            type="checkbox"
                                            name="buttonGradientAnimation"
                                            checked={editData.buttonGradientAnimation || false}
                                            onChange={handleInputChange}
                                            className="gradient-toggle-input"
                                            id="buttonGradientAnimationToggle"
                                        />
                                        <label htmlFor="buttonGradientAnimationToggle" className="gradient-toggle-switch">
                                            <span className="gradient-toggle-slider"></span>
                                        </label>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* 🌟 SHADOW SECTION */}
                    <div className="shadow-section">
                        <div className="shadow-toggle-row">
                            <label className="shadow-toggle-label">Enable Shadow</label>
                            <div className="gradient-toggle-switch-container">
                                <input
                                    type="checkbox"
                                    name="buttonShadowEnabled"
                                    checked={editData.buttonShadowEnabled || false}
                                    onChange={handleInputChange}
                                    className="gradient-toggle-input"
                                    id="buttonShadowEnabledToggle"
                                />
                                <label htmlFor="buttonShadowEnabledToggle" className="gradient-toggle-switch">
                                    <span className="gradient-toggle-slider"></span>
                                </label>
                            </div>
                        </div>

                        {/* Shadow Controls (conditional) */}
                        {editData.buttonShadowEnabled && (
                            <div className="shadow-controls">
                                <div className="shadow-controls-grid">
                                    <div className="shadow-control-item">
                                        <SliderSection
                                            label="Intensity"
                                            fieldName="buttonShadowIntensity"
                                            value={getCurrentValue('buttonShadowIntensity', 15)}
                                            min={5}
                                            max={50}
                                            step={1}
                                            unit="px"
                                            onChange={(name, value) => handleInputChange({ target: { name, value, type: 'range' } })}
                                            color="#5C80BC"
                                            icon="/img/element.svg"
                                        />
                                    </div>
                                    
                                    <div className="shadow-control-item">
                                        <SliderSection
                                            label="Distance"
                                            fieldName="buttonShadowDistance"
                                            value={getCurrentValue('buttonShadowDistance', 8)}
                                            min={0}
                                            max={30}
                                            step={1}
                                            unit="px"
                                            onChange={(name, value) => handleInputChange({ target: { name, value, type: 'range' } })}
                                            color="#5C80BC"
                                            icon="/img/element.svg"
                                        />
                                    </div>
                                    
                                    <div className="shadow-control-item">
                                        <SliderSection
                                            label="Opacity"
                                            fieldName="buttonShadowOpacity"
                                            value={getCurrentValue('buttonShadowOpacity', 25)}
                                            min={5}
                                            max={80}
                                            step={5}
                                            unit="%"
                                            onChange={(name, value) => handleInputChange({ target: { name, value, type: 'range' } })}
                                            color="#5C80BC"
                                            icon="/img/element.svg"
                                        />
                                    </div>
                                </div>
                                
                                {/* Shadow Color */}
                                <div className="shadow-color-container">
                                    <div className="color-container">
                                        <label className="color-label">Shadow Color</label>
                                        <div className="color-picker-wrapper">
                                            <input
                                                type="color"
                                                name="buttonShadowColor"
                                                value={getCurrentValue('buttonShadowColor', '#000000')}
                                                onChange={handleInputChange}
                                                className="hidden-color-input"
                                                id="buttonShadowColorPicker"
                                            />
                                            <label htmlFor="buttonShadowColorPicker" className="color-display-circle" 
                                                   style={{ backgroundColor: getCurrentValue('buttonShadowColor', '#000000') }}>
                                            </label>
                                        </div>
                                        <input
                                            type="text"
                                            className="hex-input"
                                            value={colorToHex(getCurrentValue('buttonShadowColor', '#000000'))}
                                            onChange={(e) => handleHexChange('buttonShadowColor', e.target.value)}
                                            placeholder="#000000"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* 📱 DEVICE SELECTOR - EINMAL FÜR ALLE SETTINGS */}
                    <div className="button-settings-group">
                        <div className="settings-group-header">
                            <i className="fa fa-mobile-alt"></i>
                            <span>Device Settings</span>
                        </div>
                        
                        <div className="device-toggle-container">
                            <div className="device-toggle">
                                <button
                                    type="button"
                                    className={`device-toggle-btn ${activeDevice === 'desktop' ? 'active' : ''}`}
                                    onClick={() => handleDeviceSwitch('desktop')}
                                >
                                    <i className="fa fa-desktop"></i>
                                    Desktop
                                </button>
                                <button
                                    type="button"
                                    className={`device-toggle-btn ${activeDevice === 'mobile' ? 'active' : ''}`}
                                    onClick={() => handleDeviceSwitch('mobile')}
                                >
                                    <i className="fa fa-mobile-alt"></i>
                                    Mobile
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* 📐 DIMENSIONS SECTION */}
                    <div className="button-settings-group">
                        <div className="settings-group-header">
                            <i className="fa fa-expand"></i>
                            <span>Dimensions ({activeDevice})</span>
                        </div>
                        
                        {/* ✅ VEREINFACHTE BUTTON WIDTH LOGIK */}
                        {activeDevice === 'desktop' ? (
                            /* Desktop: Direkte Pixel-Angabe */
                            <div className="button-setting-item">
                                <SliderSection
                                    label="Width (px)"
                                    fieldName="buttonWidthDesktop"
                                    value={getCurrentValue('buttonWidthDesktop', editData.buttonWidth || 200)}
                                    min={80}
                                    max={600}
                                    step={10}
                                    unit="px"
                                    onChange={(name, value) => handleDeviceChange('buttonWidthDesktop', value)}
                                    color="#5C80BC"
                                    icon="/img/width.svg"
                                />
                            </div>
                        ) : (
                            /* Mobile: Prozent + Max Width System */
                            <>
                                <div className="button-setting-item">
                                    <SliderSection
                                        label="Width (%)"
                                        fieldName="buttonWidthMobile"
                                        value={getCurrentValue('buttonWidthMobile', 100)}
                                        min={20}
                                        max={100}
                                        step={5}
                                        unit="%"
                                        onChange={(name, value) => handleDeviceChange('buttonWidthMobile', value)}
                                        color="#5C80BC"
                                        icon="/img/width.svg"
                                    />
                                </div>
                                
                                <div className="button-setting-item">
                                    <SliderSection
                                        label="Max Width (px)"
                                        fieldName="buttonMaxWidthMobile"
                                        value={getCurrentValue('buttonMaxWidthMobile', 280)}
                                        min={100}
                                        max={500}
                                        step={10}
                                        unit="px"
                                        onChange={(name, value) => handleDeviceChange('buttonMaxWidthMobile', value)}
                                        color="#5C80BC"
                                        icon="/img/width.svg"
                                    />
                                </div>
                            </>
                        )}

                        {/* Height */}
                        <div className="button-setting-item">
                            <SliderSection
                                label="Height"
                                fieldName={getDeviceField('buttonHeight')}
                                value={getCurrentValue('buttonHeight', 50)}
                                min={30}
                                max={80}
                                step={2}
                                unit="px"
                                onChange={(name, value) => handleDeviceChange('buttonHeight', value)}
                                color="#5C80BC"
                                icon="/img/height.svg"
                            />
                        </div>

                        {/* Border Radius */}
                        <div className="button-setting-item">
                            <SliderSection
                                label="Border Radius"
                                fieldName={getDeviceField('buttonBorderRadius')}
                                value={getCurrentValue('buttonBorderRadius', 8)}
                                min={0}
                                max={50}
                                step={1}
                                unit="px"
                                onChange={(name, value) => handleDeviceChange('buttonBorderRadius', value)}
                                color="#5C80BC"
                                icon="/img/element.svg"
                            />
                        </div>
                    </div>

                    {/* 📏 SPACING SETTINGS */}
                    <div className="button-settings-group">
                        <div className="settings-group-header">
                            <i className="fa fa-arrows-v"></i>
                            <span>Spacing</span>
                        </div>
                        
                        {/* Margin Top */}
                        <div className="button-setting-item">
                            <SliderSection
                                label="Margin Top"
                                fieldName="buttonMarginTop"
                                value={editData.buttonMarginTop ?? 30}
                                min={-50}
                                max={50}
                                step={5}
                                unit="px"
                                onChange={(name, value) => {
                                    const fakeEvent = {
                                        target: { name: 'buttonMarginTop', value: value, type: 'range' }
                                    };
                                    handleInputChange(fakeEvent);
                                }}
                                color="#5C80BC"
                                icon="/img/element.svg"
                            />
                        </div>

                        {/* Margin Bottom */}
                        <div className="button-setting-item">
                            <SliderSection
                                label="Margin Bottom"
                                fieldName="buttonMarginBottom"
                                value={editData.buttonMarginBottom ?? 0}
                                min={-50}
                                max={50}
                                step={5}
                                unit="px"
                                onChange={(name, value) => {
                                    const fakeEvent = {
                                        target: { name: 'buttonMarginBottom', value: value, type: 'range' }
                                    };
                                    handleInputChange(fakeEvent);
                                }}
                                color="#5C80BC"
                                icon="/img/element.svg"
                            />
                        </div>
                    </div>

                    {/* 🔤 TEXT SETTINGS */}
                    <div className="button-settings-group">
                        <div className="settings-group-header">
                            <i className="fa fa-font"></i>
                            <span>Text ({activeDevice})</span>
                        </div>
                        
                        {/* Button Text */}
                        <div className="button-setting-item">
                            <div className="setting-label-row">
                                <label className="setting-label">Button Text</label>
                            </div>
                            <input
                                type="text"
                                name={buttonConfig.textField}
                                value={getCurrentValue(buttonConfig.textField, 'Button Text')}
                                onChange={handleInputChange}
                                className="button-link-input"
                                placeholder="Enter button text..."
                            />
                        </div>

                        {/* Text Size */}
                        <div className="button-setting-item">
                            <SliderSection
                                label="Text Size"
                                fieldName={getDeviceField('buttonTextSize')}
                                value={getCurrentValue('buttonTextSize', 16)}
                                min={12}
                                max={24}
                                step={1}
                                unit="px"
                                onChange={(name, value) => handleDeviceChange('buttonTextSize', value)}
                                color="#5C80BC"
                                icon="/img/text.svg"
                            />
                        </div>

                        {/* Text Weight */}
                        <div className="button-setting-item">
                            <div className="setting-label-row">
                                <label className="setting-label">Text Weight</label>
                            </div>
                            <div className="weight-selector">
                                {['300', '400', '500', '600', '700', '800'].map(weight => (
                                    <button
                                        key={weight}
                                        type="button"
                                        className={`weight-option ${getCurrentValue('buttonTextWeight', '600') === weight ? 'selected' : ''}`}
                                        onClick={() => handleDeviceChange('buttonTextWeight', weight)}
                                    >
                                        {weight}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* ✅ FONT FAMILY SELECTOR - Wiederverwendbarer FontSelector */}
                        <div className="button-setting-item">
                            <FontSelector
                                label="Font Family"
                                fieldName="buttonFont"
                                currentFont={getCurrentValue('buttonFont', '')}
                                onChange={handleInputChange}
                                fontType="small" // ✅ Button verwendet Small Font Pool
                            />
                        </div>
                    </div>

                    {/* 🔲 BORDER & EFFECTS */}
                    <div className="button-settings-group">
                        <div className="settings-group-header">
                            <i className="fa fa-border-style"></i>
                            <span>Border & Effects</span>
                        </div>
                        
                        {/* Enable Border Toggle */}
                        <div className="button-setting-item">
                            <div className="setting-label-row">
                                <label className="setting-label">Enable Border</label>
                                <div className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        name="buttonBorderEnabled"
                                        checked={editData.buttonBorderEnabled || false}
                                        onChange={handleInputChange}
                                        className="toggle-input"
                                        id="buttonBorderEnabledToggle"
                                    />
                                    <label htmlFor="buttonBorderEnabledToggle" className="toggle-slider"></label>
                                </div>
                            </div>
                        </div>

                        {/* Border Width (only if border enabled) */}
                        {editData.buttonBorderEnabled && (
                            <>
                                <div className="button-setting-item">
                                    <SliderSection
                                        label="Border Width"
                                        fieldName="buttonBorderWidth"
                                        value={getCurrentValue('buttonBorderWidth', 2)}
                                        min={1}
                                        max={10}
                                        step={1}
                                        unit="px"
                                        onChange={(name, value) => handleInputChange({ target: { name, value, type: 'range' } })}
                                        color="#5C80BC"
                                        icon="/img/element.svg"
                                    />
                                </div>
                                
                                <div className="color-container">
                                    <label className="color-label">Border Color</label>
                                    <div className="color-picker-wrapper">
                                        <input
                                            type="color"
                                            name="buttonBorderColor"
                                            value={getCurrentValue('buttonBorderColor', '#5C80BC')}
                                            onChange={handleInputChange}
                                            className="hidden-color-input"
                                            id="buttonBorderColorPicker"
                                        />
                                        <label htmlFor="buttonBorderColorPicker" className="color-display-circle" 
                                               style={{ backgroundColor: getCurrentValue('buttonBorderColor', '#5C80BC') }}>
                                        </label>
                                    </div>
                                    <input
                                        type="text"
                                        className="hex-input"
                                        value={colorToHex(getCurrentValue('buttonBorderColor', '#5C80BC'))}
                                        onChange={(e) => handleHexChange('buttonBorderColor', e.target.value)}
                                        placeholder="#5C80BC"
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    {/* 🎬 ANIMATIONS SECTION */}
                    <div className="button-settings-group">
                        <div className="settings-group-header">
                            <i className="fa fa-play"></i>
                            <span>Animations</span>
                        </div>
                        
                        {/* Enable Animations Toggle */}
                        <div className="button-setting-item">
                            <div className="setting-label-row">
                                <label className="setting-label">Enable Animations</label>
                                <div className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        name="buttonAnimationsEnabled"
                                        checked={editData.buttonAnimationsEnabled || false}
                                        onChange={handleInputChange}
                                        className="toggle-input"
                                        id="buttonAnimationsEnabledToggle"
                                    />
                                    <label htmlFor="buttonAnimationsEnabledToggle" className="toggle-slider"></label>
                                </div>
                            </div>
                        </div>

                        {/* Animation Controls (conditional) */}
                        {editData.buttonAnimationsEnabled && (
                            <>
                                <div className="button-setting-item">
                                    <div className="setting-label-row">
                                        <label className="setting-label">Animation Type</label>
                                    </div>
                                    <select
                                        name="buttonAnimationType"
                                        value={getCurrentValue('buttonAnimationType', 'gentle-pulse')}
                                        onChange={handleInputChange}
                                        className="button-link-input"
                                    >
                                        {buttonAnimations.map(animation => (
                                            <option key={animation.id} value={animation.id}>
                                                {animation.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                
                                <div className="button-setting-item">
                                    <SliderSection
                                        label="Animation Speed"
                                        fieldName="buttonAnimationSpeed"
                                        value={getCurrentValue('buttonAnimationSpeed', 2000)}
                                        min={800}
                                        max={4000}
                                        step={200}
                                        unit="ms"
                                        onChange={(name, value) => handleInputChange({ target: { name, value, type: 'range' } })}
                                        color="#5C80BC"
                                        icon="/img/element.svg"
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    {/* 🔗 BUTTON LINK */}
                    <div className="button-settings-group">
                        <div className="settings-group-header">
                            <i className="fa fa-link"></i>
                            <span>Link</span>
                        </div>
                        
                        <div className="button-setting-item">
                            <div className="setting-label-row">
                                <label className="setting-label">Button Link</label>
                            </div>
                            <input
                                type="url"
                                name={buttonConfig.linkField}
                                value={getCurrentValue(buttonConfig.linkField, '')}
                                onChange={handleInputChange}
                                className="button-link-input"
                                placeholder="https://example.com"
                            />
                        </div>
                    </div>

                </div>
            )}
        </div>
    );
}

export default ButtonWidgetStyle;